<template>
  <div
    class="items-start justify-center text-gray-800 flex flex-wrap organism product-grid gap-8"
  >
    <div
      v-for="item in gridItems"
      class="z-0 w-full max-w-sm mb-2 text-center item"
      :data-base-price="item.basePrice"
      :data-collection="item.collection"
      :data-type="item.type"
      :data-location="item.location"
      :data-pbc="item.priceByCommunity"
      :data-id="item.id"
      :key="item.id"
    >
      <NuxtLink :to="item.link">
        <h3 class="pb-2 text-2xl font-semibold font-archer whitespace-pre-line">
          {{ item.title }}
        </h3>
        <div
          class="border border-neutral-400 drop-shadow-lg hover:drop-shadow-md"
        >
          <figure class="relative w-full h-56">
            <img
              v-bind="getResponsiveImageAttrs(item.thumbnailImage)"
              class="object-cover w-full h-full"
            />
            <span
              v-if="item.statusMessage?.length"
              class="absolute left-0 px-4 py-[5px] text-white uppercase top-6 font-ubuntu bg-snipe-blue"
            >
              {{
                item.statusMessage[1] === "Custom Message"
                  ? item.statusCustom
                  : item.statusMessage[1]
              }}
            </span>
            <span
              v-if="item.price"
              :class="[
                'absolute right-0 px-4 py-[5px] text-white uppercase top-6 font-ubuntu bg-bc-purple',
                item.isBasePrice ? 'base-price' : '',
              ]"
            >
              ${{ item.price }}
            </span>
          </figure>
          <div
            v-if="item.features"
            class="bg-[#f1f1f1] font-ubuntu text-lg italic font-light p-3 leading-none"
          >
            <span v-if="item.features?.length === 1">
              {{ item.features[0].feature }}
            </span>
            <span v-else v-for="feature in item.features" class="feature-list">
              {{ feature.feature }}
            </span>
          </div>
          <div
            class="flex px-2 pt-3 pb-4 bg-white justify-evenly details gap-4 empty:p-0"
          >
            <div v-if="item.beds" class="flex flex-col">
              <span class="text-[22px]">
                {{ item.beds }}
              </span>
              <span class="text-xs font-bold font-arial">BEDS</span>
            </div>
            <div v-if="item.baths" class="flex flex-col">
              <span class="text-[22px]">
                {{ item.baths }}
              </span>
              <span class="text-xs font-bold font-arial">BATH</span>
            </div>
            <div v-if="item.sqft" class="flex flex-col">
              <span class="text-[22px]">
                {{ item.sqft }}
              </span>
              <span class="text-xs font-bold font-arial">SQ. FT.</span>
            </div>
          </div>
        </div>
      </NuxtLink>
    </div>
  </div>
</template>

<style>
.product-grid {
  &.item-count-1,
  &.item-count-2 {
  }
  .feature-list {
    &::after {
      content: ", ";
    }
    &:last-of-type::after {
      @apply hidden;
    }
  }

  span.base-price::before {
    @apply content-['from'] lowercase block text-xs leading-none font-archer italic -mb-1 text-right;
  }
}
</style>

<script setup>
const getResponsiveImageAttrs = (image) => useResponsiveImage(image, "medium");

const props = defineProps({
  organism: Array,
  type: String,
  neighborhood: Object,
  preserveOrder: Boolean, // When true, skip automatic sorting (for manually ordered floorplans)
});

const route = useRoute();

const gridItems = computed(() => {
  const sortByPriceRoute =
    route.path === "/homes/westminster-co/the-knolls" ||
    route.path === "/homes/westminster-co/the-knolls/";

  switch (props.type) {
    case "model-homes":
      return Array.isArray(props.organism)
        ? props.organism.map((item) => {
            return {
              link: item.uri,
              title: item.title,
              thumbnail: getImageSrc(
                item.modelDetails?.elevations[0],
                "medium",
              ),
              thumbnailImage: item.modelDetails?.elevations[0],
              statusMessage: item.productStatus?.statusMessage,
              statusCustom: item.productStatus?.statusCustom,
              features:
                item.modelDetails?.floorplan?.floorplanDetails?.features,
              beds: detailMinMax(
                item.modelDetails?.floorplan?.floorplanDetails?.bedroomsMin,
                item.modelDetails?.floorplan?.floorplanDetails?.bedroomsMax,
              ),
              baths: null,
              sqft: detailMinMax(
                item.modelDetails?.floorplan?.floorplanDetails?.mainSqrFt,
                item.modelDetails?.floorplan?.floorplanDetails?.finishedSqrFt,
              ),
            };
          })
        : [];
    case "floorplans":
      const getFloorplanPrice = (planPriceByCommunity, basePrice) => {
        const communityPrice = planPriceByCommunity?.find((community) => {
          return (
            community.neighborhood.databaseId === props.neighborhood.databaseId
          );
        }).price;
        return communityPrice
          ? { price: communityPrice.toLocaleString(), isBasePrice: false }
          : { price: basePrice?.toLocaleString(), isBasePrice: true };
      };

      const items = Array.isArray(props.organism)
        ? props.organism
            .filter((item) => !isPlanHidden(item))
            .map((item) => {
            const floorPlanPrice = getFloorplanPrice(
              item.floorplanDetails?.planPriceByCommunity,
              item.floorplanDetails?.basePrice,
            );
            return {
              id: item.databaseId,
              link: item.uri,
              title: item.floorplanDetails?.displayTitle ?? item.title,
              thumbnail: getImageSrc(
                item.floorplanDetails?.elevations[0],
                "medium",
              ),
              thumbnailImage: item.floorplanDetails?.elevations[0],
              statusMessage: item.productStatus?.statusMessage,
              statusCustom: item.productStatus?.statusCustom,
              price: floorPlanPrice.price,
              isBasePrice: floorPlanPrice.isBasePrice,
              priceByCommunity:
                item.floorplanDetails?.planPriceByCommunity?.[0]?.price.toLocaleString(),
              features: item.floorplanDetails?.features,
              beds: detailMinMax(
                item.floorplanDetails?.bedroomsMin,
                item.floorplanDetails?.bedroomsMax,
              ),
              baths: null,
              sqft: detailMinMax(
                item.floorplanDetails?.mainSqrFt,
                item.floorplanDetails?.finishedSqrFt,
              ),
              basePrice: item.floorplanDetails?.basePrice ?? false,
              collection: item.floorplanDetails?.collection,
              type: item.floorplanDetails?.type,
              location:
                item.floorplanDetails?.neighborhood?.neighborhood?.city,
            };
          })
        : [];

      // Skip sorting if preserveOrder is true (manually ordered floorplans)
      if (props.preserveOrder) {
        return items;
      }

      if (sortByPriceRoute) {
        const sorted = items.sort((a, b) => {
          const priceA = a.price ? parseInt(a.price.replace(/,/g, "")) : null;
          const priceB = b.price ? parseInt(b.price.replace(/,/g, "")) : null;

          if (priceA === null) return 1;
          if (priceB === null) return -1;
          return priceA - priceB;
        });

        return sorted;
      }

      // Existing sorting logic for other routes
      return items.sort((a, b) => {
        const numA = getFirstNumericValue(a.title);
        const numB = getFirstNumericValue(b.title);

        if (numA !== undefined && numB !== undefined) {
          if (numA !== numB) {
            return numA - numB;
          } else {
            const baseA = stripTrailingPlus(a.title);
            const baseB = stripTrailingPlus(b.title);

            if (baseA === baseB) {
              const aHasPlus = a.title.trim().endsWith("+");
              const bHasPlus = b.title.trim().endsWith("+");
              if (aHasPlus && !bHasPlus) return 1;
              if (!aHasPlus && bHasPlus) return -1;
              return 0;
            } else {
              return baseA.localeCompare(baseB);
            }
          }
        } else if (numA !== undefined) {
          return -1;
        } else if (numB !== undefined) {
          return 1;
        } else {
          return a.title.localeCompare(b.title);
        }
      });

    case "quick-move-in":
      const qmiItems = Array.isArray(props.organism)
        ? props.organism.map((item) => {
            // Build custom title based on page context
            const address = item.qmiHomeDetails?.address1 || "";
            const city = item.qmiHomeDetails?.city || "";
            const floorplanTitle = item.qmiHomeDetails?.floorplan?.title || "";

            let customTitle;
            if (props.neighborhood) {
              // On neighborhood pages: [address1]:\n[floorplan title]
              customTitle =
                address && floorplanTitle
                  ? `${address}:\n${floorplanTitle}`
                  : item.title;
            } else {
              // On all other pages: [address1], [city]:\n[floorplan title]
              customTitle =
                address && city && floorplanTitle
                  ? `${address}, ${city}:\n${floorplanTitle}`
                  : item.title;
            }

            return {
              link: item.uri,
              title: customTitle,
              thumbnail: getImageSrc(
                item.qmiHomeDetails?.elevations[0],
                "medium",
              ),
              thumbnailImage: item.qmiHomeDetails?.elevations[0],
              statusMessage: item.productStatus?.statusMessage,
              statusCustom: item.productStatus?.statusCustom,
              price: item.qmiHomeDetails?.price
                ? parseInt(item.qmiHomeDetails?.price).toLocaleString()
                : false,
              plan: item.qmiHomeDetails?.floorplan?.title,
              features:
                item.qmiHomeDetails?.floorplan?.floorplanDetails?.features,
              beds: item.qmiHomeDetails?.bedrooms,
              baths: item.qmiHomeDetails?.bathrooms,
              sqft: item.qmiHomeDetails?.finishedSqrFt,
            };
          })
        : [];

      if (sortByPriceRoute) {
        return qmiItems.sort((a, b) => {
          const priceA = a.price ? parseInt(a.price.replace(/,/g, "")) : null;
          const priceB = b.price ? parseInt(b.price.replace(/,/g, "")) : null;

          if (priceA === null) return 1;
          if (priceB === null) return -1;
          return priceA - priceB;
        });
      }

      return qmiItems;
  }
});
</script>
